'use client'

// components/LeadForms/TwoLeadForm.tsx
import { Spinner } from "@components/atoms";
import PhoneInput from "@components/atoms/PhoneInput";
import { Anchor, Button, Group, PinInput, Text, TextInput } from "@mantine/core";
import classes from "styles/CalculatorStyles/BMICalculator.module.css";
import React, { useState, useEffect } from 'react';
import { generateId } from "@components/LeadForm/helper";

export interface TwoLeadFormProps {
    /** A unique name for the form to prefix element IDs. */
    formName: string;
    isds: Record<string, string>[];
    getQuoteForm: any;
    otp: string;
    time: number;
    showOTP: boolean;
    errors: {
        phone?: string;
        name?: string;
        otp?: string;
    };
    onMobileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onMobileReset: () => void;
    handleBackspace: () => void;
    handleOTP?: (val: string) => void;
    onGetOTP: () => void;
    onViewPlan: () => void;
    initData: any;
    isLoading: boolean;
    onCountryChange: (val: string) => void;
}

const maskPhone = (phone: string) => phone.slice(0, 5) + "••••";

const TwoLeadForm: React.FC<TwoLeadFormProps> = ({
    formName,
    getQuoteForm,
    isds,
    otp,
    time,
    showOTP,
    errors,
    onMobileChange,
    onNameChange,
    handleOTP,
    onMobileReset,
    onCountryChange,
    initData,
    onGetOTP,
    onViewPlan,
    handleBackspace,
    isLoading
}) => {
    const [isMounted, setIsMounted] = useState(false);
    const phone = getQuoteForm?.phoneNumber;
    const defaultCountry = "India";
    const hasFieldErrors = !getQuoteForm?.phoneNumber ||
        !getQuoteForm?.name?.trim() || !!errors.name || !!errors.phone || !!errors.otp;

    const isOtpInvalid = showOTP && (otp.length !== 4 || !!errors.otp);

    const isFormInvalid = hasFieldErrors || isOtpInvalid;

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Show loading state during SSR/initial render
    if (!isMounted) {
        return (
            <section className="bg-white" suppressHydrationWarning>
                <div className="flex flex-col gap-2" suppressHydrationWarning>
                    <Text fw={600} size="sm" className="mb-2">
                        Mobile Numbers
                    </Text>
                    <div className="border border-gray-300 rounded-md px-3 py-2.5 bg-white h-10"></div>
                    
                    <Text fw={600} size="sm" className="mb-2">
                        Name
                    </Text>
                    <div className="border border-gray-300 rounded-md px-3 py-2.5 bg-white h-10"></div>
                    
                    <div className="flex items-start gap-2 text-xxs opacity-60 my-2">
                        <img
                            alt="Disclaimer info"
                            loading="lazy"
                            src="/ethinos-home-page/svgs/disclaimer-info.svg"
                            height="15"
                            width="15"
                        />
                        <span>
                            By clicking Get OTP, you authorize Niva Bupa to Call/SMS/Whatsapp on your registered mobile overriding DNCR.
                        </span>
                    </div>
                    
                    <button
                        type="button"
                        disabled
                        className="bg-orange-300 text-white cursor-not-allowed rounded-md w-full px-10 py-3"
                    >
                        Get OTP
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-white">
            <div className="flex flex-col gap-2">
                <Text fw={600} size="sm" className="mb-2">
                    Mobile Numbers
                </Text>
                {/* Mobile Number */}
                <PhoneInput
                    id={generateId(formName, "mobile-number-input")}
                    isds={isds}
                    inputValue={getQuoteForm?.phoneNumber}
                    countryValue={getQuoteForm?.country || defaultCountry}
                    onMobileChange={onMobileChange}
                    onCountryChange={onCountryChange}
                    isMask={initData?.channel == "Tele Caller" || false}
                    onMobileReset={onMobileReset}
                    newDesignConfig={false}
                    isLeadForm
                />
                {errors.phone && (
                    <Text size="xs" color="red">
                        {errors.phone}
                    </Text>
                )}

                <Text fw={600} size="sm" className="mb-2">
                    Name
                </Text>
                {/* Name */}
                <TextInput
                    id={generateId(formName, "name-input")}
                    placeholder="Enter your name"
                    size="sm"
                    radius="md"
                    classNames={classes}
                    value={getQuoteForm?.name}
                    className="rounded-md focus:outline-none focus:ring-0 text-base"
                    onChange={onNameChange}
                />
                {errors.name && (
                    <Text size="xs" color="red">
                        {errors.name}
                    </Text>
                )}

                {/* OTP Section */}
                {showOTP && (
                    <>
                        <Text fw={600} size="sm" className="mb-2">
                            Enter the verification code
                        </Text>
                        <Text size="xs" color="dimmed">
                            [OTP has been sent to {maskPhone(phone)}]
                        </Text>

                        <Group align="center" mb="sm">
                            <PinInput
                                id={generateId(formName, "otp-input")}
                                type="number"
                                length={4}
                                value={otp}
                                onChange={handleOTP}
                                size="lg"
                                onKeyDown={(e) => {
                                    if (e.key === "Backspace") {
                                        e.preventDefault();
                                        handleBackspace();
                                    }
                                }}
                            />
                        </Group>
                        {errors.otp && (
                            <Text size="xs" color="red">
                                {errors.otp}
                            </Text>
                        )}
                        <Text size="xs" color="dimmed" mb="sm">
                            {time > 0 ? (
                                <>Resend the code in {time}s</>
                            ) : (
                                <Anchor component="button" size="xs" onClick={onGetOTP} id={generateId(formName, "resend-otp-button")}>
                                    Resend code
                                </Anchor>
                            )}
                        </Text>
                    </>
                )}

                {/* Disclaimer */}
                <div className="flex items-start gap-2 text-xxs opacity-60 my-2">
                    <img
                        alt="Disclaimer info"
                        loading="lazy"
                        src="/ethinos-home-page/svgs/disclaimer-info.svg"
                        height="15"
                        width="15"
                    />
                    <span>
                        By clicking {showOTP ? "Get Quote" : "Get OTP"}, you authorize Niva
                        Bupa to Call/SMS/Whatsapp on your registered mobile overriding DNCR.
                    </span>
                </div>

                {/* Action Button */}
                <Button
                    id={generateId(formName, showOTP ? "get-quote-button" : "get-otp-button")}
                    fullWidth
                    disabled={isFormInvalid || isLoading}
                    onClick={showOTP ? onViewPlan : onGetOTP}
                    className={`hover:opacity-90 transition-opacity ${isFormInvalid || isLoading
                        ? "bg-orange-300 text-white cursor-not-allowed"
                        : "bg-nivabupa-ethinosHomeOrange text-white rounded-md w-full px-10 py-3 hover:opacity-90 transition-opacity"}`}
                >
                    {isLoading ? <Spinner /> : showOTP ? "Get Quote" : "Get OTP"}
                </Button>
            </div>
        </section>
    );
};

export default TwoLeadForm;